import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { CBadge, CLink, CText } from '@carrefour/design-system-components-react';
import CaCard from '../../CaCard';
import CommercialCardDesktop from '../../CommercialCardDesktop';
import { useKpiVisibility } from '../../../kpis/useKpiVisibility';
import { CommercialUnitData, TurnoverObjective } from '../../../types/commercial';
import { DriveOrders, DriveTurnover } from '../../../types/drive';
import { useTranslation } from 'react-i18next';
import { InfoBullType } from '../../../types/infoBull';
import { formatKiloEuro, formatPercentageVal } from '../../../utils/numberUtils';
import DeltaBadge from '../../DeltaBadge';
import { computeRHRate } from '../../../utils/turnover';
import MrclText from '../../marcel/MrclText';

export interface LandingCommercialCardProps {
  directTurnoverData?: CommercialUnitData | null;
  directDriveTurnoverData?: DriveTurnover | null;
  pastTurnoverData?: CommercialUnitData | null;
  pastDriveTurnoverData?: DriveTurnover | null;
  directObjectiveTurnoverData?: TurnoverObjective | null;
  currentDriveObjectiveData?: TurnoverObjective | null;
  currentEcommerceTurnoverData?: DriveTurnover | null;
  pastEcommerceTurnoverData?: DriveTurnover | null;
  currentDriveOrdersData?: DriveOrders | null;
  currentLadOrdersData?: DriveOrders | null;
  lastYearDriveOrdersData?: DriveOrders | null;
  lastYearLadOrdersData?: DriveOrders | null;
}

const CardWrapper = styled.div`
  background-color: #ffffff;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 640px),
    only screen and (max-height: 560px) and (orientation: landscape) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LiveBadge = styled(CBadge)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 5px;
  border: 1px solid #047857;
  background-color: #e6f4ea;
  color: #047857;
  font-size: 14px;
  font-weight: 600;
`;

const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #047857;
`;

const ValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
`;

const MainValue = styled.span`
  font-size: 48px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
`;

const MainUnit = styled.span`
  font-size: 24px;
  font-weight: 700;
  margin-left: 2px;
`;

const TrendLabel = styled(CText)`
  font-size: 14px;
  color: #6b7280;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 240px;
  align-self: flex-end;
  margin-bottom: -6px;

  @media (max-width: 640px),
    only screen and (max-height: 560px) and (orientation: landscape) {
    align-self: flex-start;
    min-width: unset;
    width: 100%;
    margin-bottom: 0;
  }
`;

const ProgressBarTrack = styled.div`
  width: 350px;
  height: 8px;
  border-radius: 9999px;
  background-color: #fce8e6;
  overflow: hidden;
  display: flex;

  @media (max-width: 640px),
    only screen and (max-height: 560px) and (orientation: landscape) {
    width: 100%;
    max-width: 350px;
  }
`;

const ProgressBarFill = styled.div<{ $percent: number; $isNegative?: boolean }>`
  width: ${(props) => Math.min(Math.max(Math.abs(props.$percent), 0), 100)}%;
  height: 100%;
  background-color: ${({ $isNegative }) => ($isNegative ? '#d32f2f' : '#2e7d32')};
  border-radius: 9999px;
  transition: width 0.3s ease-in-out;
`;

const GoalText = styled(CText)`
  && {
    font-size: 12px;
  }
  color: #4b5563;
  text-align: left;
  width: 100%;
`;

const DetailLink = styled(CLink)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #0970e6;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const PerformanceHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const PerformanceMagasinCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  align-items: stretch;
  grid-auto-rows: minmax(0, 1fr);
  flex: 1;

  > * {
    border: 1px solid #e5e7eb;
    min-height: 178px;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 768px),
    only screen and (orientation: landscape) and (max-height: 700px) and (max-width: 1024px),
    only screen and (max-height: 560px) and (orientation: landscape),
    only screen and (max-width: 960px) and (orientation: landscape) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
`;

const PerformanceSectionsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  align-items: stretch;

  @media (max-width: 1024px),
    only screen and (max-height: 560px) and (orientation: landscape) {
    grid-template-columns: 1fr;
  }
`;

const PerformanceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  height: 100%;
`;

const EcommerceMetricCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  min-height: 178px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 10px;
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid #f3f4f6;
  margin: 0;
  width: 100%;
`;

export interface LandingCommercialData {
  directTurnover?: number;
  directDriveTurnover?: number;
  pastTurnover?: number;
  pastDriveTurnover?: number;
  directObjective?: number;
  driveObjective?: number;
}

export const calculateLandingCommercialMetrics = ({
  directTurnover = 0,
  directDriveTurnover = 0,
  pastTurnover = 0,
  pastDriveTurnover = 0,
  directObjective = 0,
  driveObjective = 0,
}: LandingCommercialData) => {
  const totalCA = directTurnover + directDriveTurnover;
  const totalCAPast = pastTurnover + pastDriveTurnover;
  const targetGoalAmount = directObjective + driveObjective;
  const diffTotalCaToObjective = totalCA - targetGoalAmount;
  const totalCaProgress = totalCAPast !== 0 ? (totalCA - totalCAPast) / totalCAPast : 0;
  const progressPercent = targetGoalAmount > 0 ? Math.round((totalCA / targetGoalAmount) * 100) : 0;

  return {
    totalCA: formatKiloEuro(totalCA),
    totalCaProgress: formatPercentageVal(totalCaProgress),
    diffTotalCaToObjective: formatKiloEuro(diffTotalCaToObjective, true),
    targetGoalAmount: formatKiloEuro(targetGoalAmount),
    progressPercent,
  };
};

export const parseProgressValue = (val?: string | number | null): number | null => {
  if (val == null) return null;
  if (typeof val === 'number') return val;

  const normalized = String(val).replace(/[−–]/g, '-').trim();
  // Matches signed or unsigned decimal numbers, e.g. "-9.4", "+19,5", "(19,5)", "19.5%"
  const match = normalized.match(/([+-]?\s*\d+(?:[.,]\d+)?)/);
  if (!match) return null;

  const cleanNumberStr = match[1].replace(/\s+/g, '').replace(',', '.');
  const num = parseFloat(cleanNumberStr);
  return isNaN(num) ? null : num;
};

const computeOrdersTotal = (data?: DriveOrders | null): number => {
  if (!data) return 0;

  return (
    (data.inProgress || 0) +
    (data.toPrepare || 0) +
    (data.ready || 0) +
    (data.delivered || 0)
  );
};

export const LandingCommercialCard: React.FC<LandingCommercialCardProps> = ({
  directTurnoverData,
  directDriveTurnoverData,
  pastTurnoverData,
  pastDriveTurnoverData,
  directObjectiveTurnoverData,
  currentDriveObjectiveData,
  currentEcommerceTurnoverData,
  pastEcommerceTurnoverData,
  currentDriveOrdersData,
  currentLadOrdersData,
  lastYearDriveOrdersData,
  lastYearLadOrdersData,
}) => {
  const navigate = useNavigate();
  const metrics = useMemo(() => {
    return calculateLandingCommercialMetrics({
      directTurnover: directTurnoverData?.turnover,
      directDriveTurnover: directDriveTurnoverData?.turnover,
      pastTurnover: pastTurnoverData?.turnover,
      pastDriveTurnover: pastDriveTurnoverData?.turnover,
      directObjective: directObjectiveTurnoverData?.objectiveAmount,
      driveObjective: currentDriveObjectiveData?.objectiveAmount,
    });
  }, [
    directTurnoverData,
    directDriveTurnoverData,
    pastTurnoverData,
    pastDriveTurnoverData,
    directObjectiveTurnoverData,
    currentDriveObjectiveData,
  ]);

  const totalCA = metrics.totalCA;
  const totalCaProgress = metrics.totalCaProgress;
  const diffTotalCaToObjective = metrics.diffTotalCaToObjective;
  const targetGoalAmount = metrics.targetGoalAmount;
  const progressPercent = metrics.progressPercent;

  const directTurnover = directTurnoverData?.turnover;
  const pastTurnover = pastTurnoverData?.turnover;
  const turnoverSdvRHRate = directTurnover != null && pastTurnover != null && pastTurnover !== 0
                          ? ((directTurnover - pastTurnover) / pastTurnover)*100
                          : undefined;

  const directBasket = directTurnoverData?.averageBasket;
  const pastBasket = pastTurnoverData?.averageBasket;
  const basketRHRate = directBasket != null && pastBasket != null && pastBasket !== 0
                      ? ((directBasket - pastBasket) / pastBasket) * 100
                      : undefined;

  const numericProgress = parseProgressValue(totalCaProgress);
  const numericDiff = parseProgressValue(diffTotalCaToObjective);
  const isObjectiveNegative =
    numericDiff !== null ? numericDiff < 0 : (progressPercent ?? 0) < 100;
  const {isKpiExists} = useKpiVisibility();
  const {t} = useTranslation();

  const totalOrders =
    computeOrdersTotal(currentDriveOrdersData) +
    computeOrdersTotal(currentLadOrdersData);

  const totalOrdersN1 =
    computeOrdersTotal(lastYearDriveOrdersData) +
    computeOrdersTotal(lastYearLadOrdersData);

  const ordersEvolution =
    totalOrdersN1 > 0 ? (computeRHRate(totalOrders, totalOrdersN1) as number | null) : null;

  const ecommerceTurnoverRate = computeRHRate(
    currentEcommerceTurnoverData?.turnover as number,
    pastEcommerceTurnoverData?.turnover as number
  );

  const currentDriveData = currentEcommerceTurnoverData;
  const driveObjective = currentDriveObjectiveData;
  const driveRHRate = ecommerceTurnoverRate;

  const totalCAMatch = totalCA?.match(/^(.*?)\s*([a-zA-Z€%]+)$/);
   
  const buildInfoBull = (title: string, data: any): InfoBullType => {
      return {
        lastUpdated: data?.importedDateTime,
        description: title,
        loading: !data,
        isUnavailable: !data || data?.turnover === null,
        error: data?.turnover === null
    };
  };
  

  return (
      <CardWrapper>
        <TopRow>
          {/* Left KPI Section */}
          <LeftSection>
            <TitleBadgeRow>
              <MrclText style={{ fontSize: '16px', fontWeight: 600, color: '#0c0c0c' }}>
                CA total temps réel
              </MrclText>
              <LiveBadge>
              <LiveDot /> Live
              </LiveBadge>
            </TitleBadgeRow>

            <ValueRow>
              <MainValue>
                {totalCAMatch ? (
                  <>
                    {totalCAMatch[1]}
                    <MainUnit>{totalCAMatch[2]}</MainUnit>
                  </>
                ) : (
                  totalCA
                )}
              </MainValue>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <DeltaBadge value={numericProgress} />
                <TrendLabel>{'vs n-1'}</TrendLabel>
              </div>
            </ValueRow>
          </LeftSection>

          {/* Right Progress Section */}
          <RightSection>
            <ProgressBarTrack>
              <ProgressBarFill
                data-testid="progress-bar-fill"
                $percent={progressPercent ?? 0}
                $isNegative={isObjectiveNegative}
              />
            </ProgressBarTrack>
            <GoalText>
              <strong style={{ color: isObjectiveNegative ? '#d32f2f' : '#2e7d32' }}>
                {diffTotalCaToObjective}
              </strong> sur un objectif de{' '}
              <strong style={{ color: '#111827' }}>{targetGoalAmount}</strong>
            </GoalText>
          </RightSection>
        </TopRow>

        <Divider />
            {/* Reserved space for performance commercial cards */}
        { isKpiExists("Commercial", "AverageBasket") && (
        <>
          <PerformanceSectionsRow>
            <PerformanceSection>
              <PerformanceHeaderRow>
                <MrclText style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                  Performance magasin
                </MrclText>
                <DetailLink onClick={() => navigate('/commercial')}>
                  Voir le détail
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </DetailLink>
              </PerformanceHeaderRow>
              <PerformanceMagasinCardsGrid data-testid="performance-magasin-cards-grid">
                <CaCard
                    title={t('commercial.sdv')}
                    target={directObjectiveTurnoverData?.objectiveAmount}
                    value={directTurnoverData?.turnover ?? 0}
                    rate={turnoverSdvRHRate as number}
                    infoBull={buildInfoBull(t('commercial.sdv'), directTurnoverData as CommercialUnitData)}
                />        
                <CommercialCardDesktop
                    title={t('commercial.panier_moyen')}
                    subTitle={t('commercial.hors_ecommerce')}
                    turnover={directTurnoverData?.averageBasket ?? 0}
                    rhRate={basketRHRate as number}
                    isLoading={!directTurnoverData}
                    currency={"€"}
                    isUnavailable={ !directTurnoverData || directTurnoverData?.averageBasket === null}
                    importedDateTime={directTurnoverData?.importedDateTime}
                    openInformationModal={() => null}
                />
              </PerformanceMagasinCardsGrid>
            </PerformanceSection>

            <PerformanceSection>
              <PerformanceHeaderRow>
                <MrclText style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                  Performance e-commerce
                </MrclText>
                <DetailLink onClick={() => navigate('/ecom')}>
                  Voir le détail
                  <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </DetailLink>
              </PerformanceHeaderRow>

              <PerformanceMagasinCardsGrid data-testid="performance-ecommerce-cards-grid">
                <EcommerceMetricCard>
                    <CaCard
                        title={t('commercial.ca_ecommerce')}
                        target={driveObjective?.objectiveAmount}
                        value={currentDriveData?.turnover ?? 0}
                        rate={driveRHRate as number}
                        infoBull={buildInfoBull(t('commercial.ca_ecommerce'), currentDriveData as DriveTurnover)}
                    />
                </EcommerceMetricCard>
                <EcommerceMetricCard>
                    <CommercialCardDesktop
                        title={t('ecommerce.orders')}
                        turnover={totalOrders}
                        rhRate={ordersEvolution ?? NaN}
                        isUnavailable={!(currentDriveOrdersData || currentLadOrdersData)}
                        importedDateTime={currentDriveOrdersData?.importedDateTime || currentLadOrdersData?.importedDateTime}
                        currency={''}
                        isLoading={false}
                        openInformationModal={() => null}
                    />
                </EcommerceMetricCard>
              </PerformanceMagasinCardsGrid>
            </PerformanceSection>
          </PerformanceSectionsRow>
        </>
        )}

      </CardWrapper>
  );
};

